import type { SavedDesign } from '../schemas/design.js'

/**
 * Fingerprint of a design document, used to recognize "the agency still has
 * exactly the design onboarding seeded" (see the client's
 * isUnconfiguredDesign). The server computes it when seeding a new agency
 * and stores it as `preferences.designSeedFingerprint`; the client compares
 * it against the fingerprint of the agency's current design.
 *
 * Not cryptographic — it only needs to be stable across JSON round-trips
 * and zod re-parses, hence the key-sorted canonical serialization.
 */
export function designFingerprint(design: SavedDesign): string {
  return `1.${hash53(canonicalStringify(design))}`
}

function canonicalStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value) ?? 'null'
  }
  if (Array.isArray(value)) {
    return `[${value.map(canonicalStringify).join(',')}]`
  }
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([k, v]) => `${JSON.stringify(k)}:${canonicalStringify(v)}`)
  return `{${entries.join(',')}}`
}

/** cyrb53: fast 53-bit string hash, rendered as hex. */
function hash53(str: string): string {
  let h1 = 0xdeadbeef
  let h2 = 0x41c6ce57
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i)
    h1 = Math.imul(h1 ^ ch, 2654435761)
    h2 = Math.imul(h2 ^ ch, 1597334677)
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507)
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909)
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507)
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909)
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16)
}
